import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import PrimaryButton from "../../shared/Button/PrimaryButton";
import { getPermissionCatalog, getSubAdmin, createSubAdmin, updateSubAdmin } from "../../redux/actions/subAdminAction";
import { hasPermission } from "../../utils/adminPermissions";

export default function SubAdminForm({ mode = "ADD" }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { permissionCatalog } = useSelector((state) => state.subAdmins);
    const { userProfile } = useSelector((state) => state.profile);
    const [loading, setLoading] = useState(mode === "EDIT");
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({ name: "", email: "", password: "", confirmPassword: "", permissions: [] });

    const canUpdateDetails = hasPermission(userProfile, "sub_admin.update");
    const canManagePermissions = hasPermission(userProfile, "sub_admin.manage_permissions");

    useEffect(() => {
        dispatch(getPermissionCatalog());
        if (mode === "EDIT" && id) {
            dispatch(getSubAdmin(id)).then((data) => {
                if (data) {
                    setForm((current) => ({ ...current, name: data.name || "", email: data.email || "", permissions: data.permissions || [] }));
                }
                setLoading(false);
            });
        }
    }, [dispatch, id, mode]);

    const groupedPermissions = useMemo(() => permissionCatalog.reduce((groups, permission) => {
        groups[permission.group] = groups[permission.group] || [];
        groups[permission.group].push(permission);
        return groups;
    }, {}), [permissionCatalog]);

    const updateField = (name, value) => setForm((current) => ({ ...current, [name]: value }));

    const togglePermission = (permission) => {
        setForm((current) => ({
            ...current,
            permissions: current.permissions.includes(permission)
                ? current.permissions.filter((item) => item !== permission)
                : [...current.permissions, permission],
        }));
    };

    const submit = (event) => {
        event.preventDefault();
        const nextErrors = {};
        if (!form.name.trim()) nextErrors.name = "Name is required";
        if (!form.email.trim()) nextErrors.email = "Email is required";
        if (mode === "ADD" && form.password.length < 8) nextErrors.password = "Password must be at least 8 characters";
        if (mode === "ADD" && form.password !== form.confirmPassword) nextErrors.confirmPassword = "Passwords do not match";
        if ((mode === "ADD" || canManagePermissions) && !form.permissions.length) nextErrors.permissions = "Select at least one permission";
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) return;

        const payload = {};
        if (mode === "ADD" || canUpdateDetails) {
            payload.name = form.name.trim();
            payload.email = form.email.trim().toLowerCase();
        }
        if (mode === "ADD" || canManagePermissions) payload.permissions = form.permissions;
        if (mode === "ADD") payload.password = form.password;

        const action = mode === "ADD"
            ? createSubAdmin(payload, () => navigate("/sub-admin"))
            : updateSubAdmin(id, payload, () => navigate("/sub-admin"));
        dispatch(action);
    };

    if (loading) return <div className="p-5">Loading sub-admin...</div>;

    return (
        <div className="col-12">
            <div className="card">
                <div className="flex justify-content-between align-items-center mb-4">
                    <h5>{mode === "ADD" ? "Create Sub-admin" : "Edit Sub-admin"}</h5>
                    <PrimaryButton label="Back" className="p-button-outlined" onClick={() => navigate("/sub-admin")} />
                </div>
                <form onSubmit={submit}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <label className="block mb-2" htmlFor="sub-admin-name">Name</label>
                            <InputText id="sub-admin-name" value={form.name} disabled={mode === "EDIT" && !canUpdateDetails} onChange={(event) => updateField("name", event.target.value)} className="w-full" />
                            {errors.name && <small className="p-error">{errors.name}</small>}
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="block mb-2" htmlFor="sub-admin-email">Email</label>
                            <InputText id="sub-admin-email" value={form.email} disabled={mode === "EDIT" && !canUpdateDetails} onChange={(event) => updateField("email", event.target.value)} className="w-full" />
                            {errors.email && <small className="p-error">{errors.email}</small>}
                        </div>
                        {mode === "ADD" && (
                            <>
                                <div className="col-12 md:col-6">
                                    <label className="block mb-2" htmlFor="sub-admin-password">Initial Password</label>
                                    <Password id="sub-admin-password" value={form.password} onChange={(event) => updateField("password", event.target.value)} toggleMask feedback={false} className="w-full" inputClassName="w-full" />
                                    {errors.password && <small className="p-error">{errors.password}</small>}
                                </div>
                                <div className="col-12 md:col-6">
                                    <label className="block mb-2" htmlFor="sub-admin-confirm-password">Confirm Password</label>
                                    <Password id="sub-admin-confirm-password" value={form.confirmPassword} onChange={(event) => updateField("confirmPassword", event.target.value)} toggleMask feedback={false} className="w-full" inputClassName="w-full" />
                                    {errors.confirmPassword && <small className="p-error">{errors.confirmPassword}</small>}
                                </div>
                            </>
                        )}
                    </div>

                    <h6 className="mt-4">Permissions</h6>
                    {!canManagePermissions && mode === "EDIT" ? (
                        <p className="text-color-secondary">You do not have permission to edit this sub-admin&apos;s permissions.</p>
                    ) : (
                        <div className="grid">
                            {Object.entries(groupedPermissions).map(([group, permissions]) => (
                                <div className="col-12 md:col-6 lg:col-4" key={group}>
                                    <div className="border-1 surface-border border-round p-3 h-full">
                                        <h6 className="mt-0">{group}</h6>
                                        {permissions.map((permission) => (
                                            <div className="flex align-items-center mb-2" key={permission.key}>
                                                <Checkbox inputId={permission.key} checked={form.permissions.includes(permission.key)} onChange={() => togglePermission(permission.key)} />
                                                <label htmlFor={permission.key} className="ml-2">{permission.label}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {errors.permissions && <small className="p-error block mt-2">{errors.permissions}</small>}
                    <div className="flex justify-content-end mt-4">
                        <PrimaryButton type="submit" label={mode === "ADD" ? "Create Sub-admin" : "Save Changes"} disabled={mode === "EDIT" && !canUpdateDetails && !canManagePermissions} />
                    </div>
                </form>
            </div>
        </div>
    );
}
